/* ═══════════════════════════════════════════════════════════════════════════
   Javna strana praćenja naloga — otvaraju je nalogodavac i prevoznik, bez
   prijave, samo preko linka.

   Renderuje se IZVAN aplikacije (vidi `src/index.js`): nema zaglavlja, sidebara
   ni Redux stanja, jer gost nije korisnik TMS-a. Strana zna samo ono što joj
   procedura pošalje — ni broj naloga ni rola se ne čitaju iz URL-a, nego se
   dobijaju iz odgovora na token. Zato ovde nema ni jedne provere prava: one su
   u `trip.menadzerPracenjeNaloga`, gde ih korisnik ne može preskočiti.

   Jezik bira sam gost — stranom prevozniku srpski ne znači ništa, a ovde nema
   prijavljenog korisnika iz čijih podešavanja bi se jezik pročitao.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './pracenje.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const ROLA = { NALOGODAVAC: 1, PREVOZNIK: 2, POSREDNIK: 3, VOZAC: 4 };

const JEZICI = [
    { kod: 'sr', ime: 'Srpski' },
    { kod: 'en', ime: 'English' },
    { kod: 'sl', ime: 'Slovenščina' },
    { kod: 'it', ime: 'Italiano' },
    { kod: 'de', ime: 'Deutsch' },
    { kod: 'fr', ime: 'Français' },
    { kod: 'ru', ime: 'Русский' },
];

/* Token je u putanji: /pracenje/<token>. Čita se iz `location`, a ne iz router
   parametara, jer strana stoji van Route stabla aplikacije. */
const uzmiToken = () => {
    const delovi = window.location.pathname.split('/').filter(Boolean);
    return delovi[0] === 'pracenje' ? (delovi[1] || '') : '';
};

const Pracenje = () => {
    const { t } = useTranslation();
    const token = useRef(uzmiToken()).current;

    const [ucitava, setUcitava] = useState(true);
    const [greska, setGreska] = useState(null);
    const [podaci, setPodaci] = useState(null);
    const [poruka, setPoruka] = useState(null);
    const [radi, setRadi] = useState(false);

    /* forma prevoznika */
    const [status, setStatus] = useState('');
    const [napomena, setNapomena] = useState('');
    const [mesto, setMesto] = useState('');
    const [koord, setKoord] = useState(null);
    const [lokacijaPoruka, setLokacijaPoruka] = useState('');
    const [izmenaId, setIzmenaId] = useState(null);

    /* forma nalogodavca */
    const [trazeni, setTrazeni] = useState('');
    const [zahtevNapomena, setZahtevNapomena] = useState('');

    const zovi = useCallback((putanja, telo) =>
        fetch(process.env.REACT_APP_BACKEND_URL + '/pracenje' + putanja, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, ...telo }),
        }).then(async r => {
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'HTTP ' + r.status);
            return d;
        }), [token]);

    const otvori = useCallback(() => {
        setUcitava(true);
        zovi('/otvori', {})
            .then(d => {
                if (d?.kod === 'LINK_NE_VAZI') { setGreska(d.message); setPodaci(null); return; }
                if (d?.message && !d.Nalog) { setGreska(d.message); setPodaci(null); return; }
                setGreska(null);
                setPodaci(d);
            })
            .catch(e => setGreska(e.message))
            .finally(() => setUcitava(false));
    }, [zovi]);

    useEffect(() => { otvori(); }, [otvori]);

    const promeniJezik = (kod) => i18n.changeLanguage(kod);

    const uzmiLokaciju = () => {
        if (!navigator.geolocation) { setLokacijaPoruka(t('pracenje.lokacijaOdbijena')); return; }
        setLokacijaPoruka('');
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setKoord({ lat: Number(p.coords.latitude.toFixed(6)), lon: Number(p.coords.longitude.toFixed(6)) });
                setLokacijaPoruka(t('pracenje.lokacijaUzeta'));
            },
            /* Odbijena dozvola nije greška nego svakodnevica: tada se mesto
               upisuje rukom, pa poruka vodi na to polje umesto da blokira unos. */
            () => setLokacijaPoruka(t('pracenje.lokacijaOdbijena')),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
    };

    const posleUpisa = (d) => {
        if (d?.kod === 'LINK_NE_VAZI') { setGreska(d.message); setPodaci(null); return; }
        setPoruka(d?.message || '');
        setStatus(''); setNapomena(''); setMesto(''); setKoord(null);
        setLokacijaPoruka(''); setIzmenaId(null);
        otvori();
    };

    const posaljiStatus = (id_zahtev = null) => {
        if (!status) { setPoruka(t('pracenje.obavezanStatus')); return; }
        setRadi(true);
        const telo = {
            statusNaloga: Number(status), napomena, lokacijaTekst: mesto,
            lat: koord?.lat ?? null, lon: koord?.lon ?? null,
        };
        const zahtev = izmenaId
            ? zovi('/izmeni', { ...telo, id: izmenaId })
            : zovi('/status', { ...telo, id_zahtev });
        zahtev.then(posleUpisa).catch(e => setPoruka(e.message)).finally(() => setRadi(false));
    };

    const posaljiZahtev = () => {
        if (!trazeni && !zahtevNapomena.trim()) { setPoruka(t('pracenje.napomenaIliStatus')); return; }
        setRadi(true);
        zovi('/zahtev', { trazeniStatus: trazeni ? Number(trazeni) : null, napomena: zahtevNapomena })
            .then(d => {
                if (d?.kod === 'LINK_NE_VAZI') { setGreska(d.message); setPodaci(null); return; }
                setPoruka(d?.message || '');
                setTrazeni(''); setZahtevNapomena('');
                otvori();
            })
            .catch(e => setPoruka(e.message))
            .finally(() => setRadi(false));
    };

    const pocniIzmenu = (s) => {
        setIzmenaId(s.id);
        setStatus(String(s.statusNaloga));
        setNapomena(s.napomena || '');
        setMesto(s.lokacija || '');
        setPoruka('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const imeRole = (r) => Number(r) === ROLA.NALOGODAVAC ? t('pracenje.rolaNalogodavac')
        : Number(r) === ROLA.PREVOZNIK ? t('pracenje.rolaPrevoznik')
        : Number(r) === ROLA.VOZAC ? t('pracenje.rolaVozac')
        : t('pracenje.rolaPosrednik');

    const birac = (
        <select className="pr-jezik" value={i18n.language?.slice(0, 2) || 'sr'}
                aria-label={t('pracenje.jezik')}
                onChange={e => promeniJezik(e.target.value)}>
            {JEZICI.map(j => <option key={j.kod} value={j.kod}>{j.ime}</option>)}
        </select>
    );

    if (ucitava) {
        return <div className="pr-koren"><div className="pr-kutija pr-sredina">{t('pracenje.ucitavanje')}</div></div>;
    }

    if (greska || !podaci) {
        return (
            <div className="pr-koren">
                <div className="pr-kutija pr-sredina">
                    <div className="pr-zaglavlje-mini">{birac}</div>
                    <i className="pi pi-lock pr-ikona-zatvoreno" />
                    <h2>{t('pracenje.linkNeVazi')}</h2>
                    <p className="pr-tiho">{t('pracenje.linkNeVaziOpis')}</p>
                </div>
            </div>
        );
    }

    const nalog = podaci.Nalog || {};
    const rola = Number(nalog.rola);
    const jePrevoznik = rola === ROLA.PREVOZNIK;
    const statusi = podaci.Statusi || [];
    const zahtevi = podaci.Zahtevi || [];
    const sifarnik = podaci.Sifarnik || [];
    const nereseni = zahtevi.filter(z => !z.resen);

    return (
        <div className="pr-koren">
            <div className="pr-kutija">

                <header className="pr-zaglavlje">
                    <div>
                        <div className="pr-posrednik">{nalog.posrednik}</div>
                        <h1>{jePrevoznik ? t('pracenje.naslovPrevoznik') : t('pracenje.naslovNalogodavac')}</h1>
                    </div>
                    {birac}
                </header>

                <section className="pr-nalog">
                    <div className="pr-broj">{nalog.brojNaloga}</div>
                    {nalog.relacija && <div className="pr-relacija">{nalog.relacija}</div>}
                    <div className="pr-datumi">
                        {nalog.datumPolaska && <span>{t('ture.datumPolaska')}: <b>{nalog.datumPolaska}</b></span>}
                        {nalog.datumDolaska && <span>{t('ture.datumDolaska')}: <b>{nalog.datumDolaska}</b></span>}
                    </div>
                    {jePrevoznik && (nalog.vozilo || nalog.vozac) && (
                        <div className="pr-datumi">
                            {nalog.vozilo && <span>{t('ture.voziloPrevoznika')}: <b>{nalog.vozilo}</b></span>}
                            {nalog.vozac && <span>{t('ture.vozacPrevoznika')}: <b>{nalog.vozac}</b></span>}
                        </div>
                    )}
                    {nalog.vaziDo && (
                        <div className="pr-rok">
                            {t('pracenje.zakljucanInfo', { vreme: nalog.vaziDo })}
                        </div>
                    )}
                </section>

                {poruka && <div className="pr-poruka">{poruka}</div>}

                {/* ── prevoznik: prijava statusa ── */}
                {jePrevoznik && (
                    <section className="pr-forma">
                        <h2>{izmenaId ? t('pracenje.ispravi') : t('pracenje.noviStatus')}</h2>

                        <label htmlFor="pr-status">{t('nova.status')}</label>
                        <select id="pr-status" value={status} onChange={e => setStatus(e.target.value)}>
                            <option value="">{t('pracenje.izaberiStatus')}</option>
                            {sifarnik.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>

                        <div className="pr-lokacija-red">
                            <button type="button" className="pr-dugme pr-tiho-dugme" onClick={uzmiLokaciju}>
                                <i className="pi pi-map-marker" /> {t('pracenje.uzmiLokaciju')}
                            </button>
                            {koord && <span className="pr-koord">{koord.lat}, {koord.lon}</span>}
                        </div>
                        {lokacijaPoruka && <div className="pr-tiho pr-mala">{lokacijaPoruka}</div>}

                        <label htmlFor="pr-mesto">{t('pracenje.lokacijaRucno')}</label>
                        <input id="pr-mesto" type="text" value={mesto} onChange={e => setMesto(e.target.value)} />

                        <label htmlFor="pr-napomena">{t('nova.napomena')}</label>
                        <textarea id="pr-napomena" rows={3} value={napomena}
                                  onChange={e => setNapomena(e.target.value)} />

                        <div className="pr-akcije">
                            <button type="button" className="pr-dugme" disabled={radi}
                                    onClick={() => posaljiStatus(null)}>
                                {izmenaId ? t('common.sacuvaj') : t('chat.send')}
                            </button>
                            {izmenaId && (
                                <button type="button" className="pr-dugme pr-tiho-dugme" disabled={radi}
                                        onClick={() => { setIzmenaId(null); setStatus(''); setNapomena(''); setMesto(''); }}>
                                    {t('common.otkazi')}
                                </button>
                            )}
                        </div>
                    </section>
                )}

                {/* ── zahtevi: prevoznik ih potvrđuje, nalogodavac vidi svoje ── */}
                {nereseni.length > 0 && (
                    <section className="pr-zahtevi">
                        <h2>{t('pracenje.zahtevi')}</h2>
                        {nereseni.map(z => (
                            <div key={z.id} className="pr-zahtev">
                                <div className="pr-zahtev-glava">
                                    <span>{z.datum}</span>
                                    {z.trazeniStatusOpis && <b>{z.trazeniStatusOpis}</b>}
                                </div>
                                {z.napomena && <div className="pr-napomena">{z.napomena}</div>}
                                {jePrevoznik && z.trazeniStatus && (
                                    <button type="button" className="pr-dugme pr-malo" disabled={radi}
                                            onClick={() => { setStatus(String(z.trazeniStatus)); posaljiStatus(z.id); }}>
                                        {t('pracenje.potvrdiZahtev')}
                                    </button>
                                )}
                            </div>
                        ))}
                    </section>
                )}

                {/* ── nalogodavac: zahtev za status ── */}
                {!jePrevoznik && (
                    <section className="pr-forma">
                        <h2>{t('pracenje.posaljiZahtev')}</h2>

                        <label htmlFor="pr-trazeni">{t('pracenje.trazeniStatus')}</label>
                        <select id="pr-trazeni" value={trazeni} onChange={e => setTrazeni(e.target.value)}>
                            <option value="">{t('pracenje.izaberiStatus')}</option>
                            {sifarnik.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>

                        <label htmlFor="pr-zahtev-napomena">{t('nova.napomena')}</label>
                        <textarea id="pr-zahtev-napomena" rows={3} value={zahtevNapomena}
                                  onChange={e => setZahtevNapomena(e.target.value)} />

                        <div className="pr-akcije">
                            <button type="button" className="pr-dugme" disabled={radi} onClick={posaljiZahtev}>
                                {t('pracenje.posaljiZahtev')}
                            </button>
                        </div>
                    </section>
                )}

                {/* ── vremenska linija ── */}
                <section className="pr-istorija">
                    <h2>{t('pracenje.istorija')}</h2>
                    {statusi.length === 0 && <p className="pr-tiho">{t('pracenje.nemaStatusa')}</p>}
                    {statusi.map(s => (
                        <div key={s.id} className="pr-korak">
                            <div className="pr-tacka" />
                            <div className="pr-korak-telo">
                                <div className="pr-korak-glava">
                                    <strong>{s.status}</strong>
                                    <span className="pr-vreme">{s.vremeStatusa}</span>
                                </div>
                                <div className="pr-meta">
                                    {t('pracenje.prijavio')}: {imeRole(s.prijavioRola)}
                                    {' · '}{t('pracenje.poslednjaPromena')}: {s.poslednjaPromena}
                                </div>
                                {s.napomena && <div className="pr-napomena">{s.napomena}</div>}
                                {(s.lokacija || (s.lat != null && s.lon != null)) && (
                                    <div className="pr-meta">
                                        <i className="pi pi-map-marker" />{' '}
                                        {s.lokacija || `${s.lat}, ${s.lon}`}
                                        {s.lat != null && s.lon != null && (
                                            <>{' · '}
                                            <a href={`https://www.google.com/maps?q=${s.lat},${s.lon}`}
                                               target="_blank" rel="noreferrer">{t('pracenje.otvoriKartu')}</a></>
                                        )}
                                    </div>
                                )}
                                {jePrevoznik && s.mozeIzmena === 1 && (
                                    <button type="button" className="pr-dugme pr-malo pr-tiho-dugme"
                                            onClick={() => pocniIzmenu(s)}>
                                        {t('pracenje.ispravi')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </section>

            </div>
        </div>
    );
};

export default Pracenje;
